import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import ApiRequest from "./ApiRequest";
import { ApiEndpoints } from "./ApiEndpoints";

const endpoints = ApiEndpoints.rbac;

const PermissionContext = createContext({
  status: "loading",
  permissionSlugs: new Set(),
  refreshPermissions: () => {},
  hasPermission: () => false,
});

export const usePermissions = () => useContext(PermissionContext);

export function PermissionProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [permissionSlugs, setPermissionSlugs] = useState(new Set());
  const [reloadKey, setReloadKey] = useState(0);

  const refreshPermissions = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      setStatus("loading");
      try {
        const response = await ApiRequest("GET", endpoints.myPermissions);
        if (cancelled) return;

        setPermissionSlugs(new Set((response.permissions || []).map((permission) => permission.slug).filter(Boolean)));
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        setPermissionSlugs(new Set());
        setStatus("ready");
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const value = useMemo(
    () => ({
      status,
      permissionSlugs,
      refreshPermissions,
      hasPermission: (slug) => (status === "ready" ? permissionSlugs.has(slug) : false),
    }),
    [status, permissionSlugs, refreshPermissions]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}
